import { Line } from "react-chartjs-2";
import { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";
import { round } from "src/app/utils/helper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
);

const zoomOptions = {
  pan: {
    enabled: true,
    mode: "x" as "x",
  },
  zoom: {
    wheel: {
      enabled: true,
    },
    pinch: {
      enabled: true,
    },
    mode: "x" as "x",
  },
};

export default function LineGradient({
  trendsName,
  speedName,
  isGradientOpposite,
  maxValue,
  minValue,
  datapoints,
  dataPointsY1,
  dataPointsY2,
  dataPointsY3,
  yLabel,
  labels,
  hideBackground,
}: any) {
  const chartRef = useRef<any>(null);
  const datasetsName = (trendsName || "")
    .split(",")
    .map((s: string) => s.trim());
  const isEngineHealthColorEnabled =
    typeof window !== "undefined"
      ? (sessionStorage.getItem("engine_health_color") ??
          localStorage.getItem("engine_health_color")) === "true"
      : false;

  const data = {
    labels: labels,
    datasets: [],
  } as any;

  data.datasets.push({
    label: datasetsName[0],
    data: datapoints,
    borderColor: "#576CBC",
    backgroundColor: "#576CBC",
    pointBackgroundColor: "#576CBC",
    yAxisID: "y",
    pointRadius: 0,
    tension: 0,
    borderWidth: 2,
  });
  // Only include the speed/right-axis dataset when there's data
  if (dataPointsY1 && dataPointsY1.length) {
    data.datasets.push({
      label: speedName,
      data: dataPointsY1,
      borderColor: "#2D4356",
      backgroundColor: "#2D4356",
      cubicInterpolationMode: "monotone" as const,
      tension: 0,
      pointBackgroundColor: "#2D4356",
      yAxisID: "y1",
      pointRadius: 0,
      hidden: false,
      borderWidth: 2,
    });
  }

  if (dataPointsY2 && dataPointsY2.length) {
    data.datasets.push({
      label: datasetsName[1] || datasetsName[0],
      data: dataPointsY2,
      borderColor: "#957777",
      backgroundColor: "#957777",
      pointBackgroundColor: "#957777",
      yAxisID: "y",
      pointRadius: 0,
      tension: 0,
      borderWidth: 2,
    });
  }
  if (dataPointsY3 && dataPointsY3.length) {
    data.datasets.push({
      label: datasetsName[2] || datasetsName[1] || datasetsName[0],
      data: dataPointsY3,
      borderColor: "#A36BFF",
      backgroundColor: "#A36BFF",
      pointBackgroundColor: "#A36BFF",
      yAxisID: "y",
      pointRadius: 0,
      tension: 0,
      borderWidth: 2,
    });
  }
  const plugins = [
    {
      id: "custom_canvas_background_color",
      type: "line",
      beforeDraw: function (chart: any, args: any, options: any) {
        const ctx = chart.ctx;
        const canvas = chart.canvas;
        const chartArea = chart.chartArea;
        let gradientBack = canvas
          .getContext("2d")
          .createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        if (!hideBackground && isEngineHealthColorEnabled) {
          if (!isGradientOpposite) {
            gradientBack.addColorStop(1, "#ffffff");
            gradientBack.addColorStop(0.41, "#ffffff");
            gradientBack.addColorStop(0.4, "#02B271");
            gradientBack.addColorStop(0, "#02B271");
          } else {
            // 1 = top of chart, 0 = bottom
            gradientBack.addColorStop(1, "#A6E58B"); // green
            gradientBack.addColorStop(0.62, "#A6E58B");
            gradientBack.addColorStop(0.58, "#E6CB6E"); // short blend into yellow
            gradientBack.addColorStop(0.38, "#E6CB6E"); // yellow
            gradientBack.addColorStop(0.3, "#EE9A50"); // orange transition
            gradientBack.addColorStop(0.22, "#F0522E"); // alarm red-orange
            gradientBack.addColorStop(0, "#F0522E");
          }

          ctx.fillStyle = gradientBack;
          ctx.fillRect(
            chartArea.left,
            chartArea.bottom,
            chartArea.right - chartArea.left,
            chartArea.top - chartArea.bottom,
          );

          if (isGradientOpposite) {
            const chartWidth = chartArea.right - chartArea.left;
            const chartHeight = chartArea.bottom - chartArea.top;

            ctx.fillStyle = "rgba(255,255,255,0.9)";
            ctx.font = "600 12px Poppins, sans-serif";
            ctx.textAlign = "center";
            ctx.fillText(
              "Alert Zone",
              chartArea.left + chartWidth / 2,
              chartArea.top + chartHeight * 0.58,
            );
            ctx.fillText(
              "Alarm Zone",
              chartArea.left + chartWidth / 2,
              chartArea.top + chartHeight * 0.82,
            );
          }
        }
      },
    },
  ];
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          font: {
            family: 'Poppins,Helvetica,"sans-serif',
            size: 14,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const datasetLabel = String(context?.dataset?.label || "");
            const chartTitle = String(trendsName || "");
            const isSpeedSeries = datasetLabel.toLowerCase() === "speed";
            const isPropellerFouling =
              (!isSpeedSeries &&
                datasetLabel.toLowerCase().includes("propeller fouling")) ||
              chartTitle.toLowerCase().includes("propeller fouling");

            const yValue = Number(context?.parsed?.y);
            if (!Number.isFinite(yValue)) {
              return `${datasetLabel}: ${context?.parsed?.y ?? ""}`;
            }

            if (isSpeedSeries) {
              return `${datasetLabel}: ${Math.round(yValue)}`;
            }

            if (isPropellerFouling) {
              return `${datasetLabel}: ${yValue.toFixed(2)}`;
            }

            return `${datasetLabel}: ${yValue}`;
          },
        },
      },
      zoom: zoomOptions,
    },
    animation: {
      duration: 0,
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 12,
            weight: "bold",
            whiteSpace: "normal",
          },
          maxTicksLimit: 7,
        },
        title: {
          text: "Time (Local Time)",
          display: true,
          font: {
            size: 14,
            family: 'Poppins,Helvetica,"sans-serif',
            weight: "bold",
          },
        },
      },
      y: {
        beginAtZero: true,
        type: "linear" as const,
        display: true,
        position: "left" as const,
        min: minValue,
        max: maxValue || 100,
        ticks: {
          padding: 7,
          font: {
            size: 12,
            weight: "bold",
          },
        },
        title: {
          padding: 7,
          text: yLabel,
          display: true,
          font: {
            size: 14,
            family: 'Poppins,Helvetica,"sans-serif',
            // weight: "bold",
          },
        },
      },
      y1:
        dataPointsY1 && dataPointsY1.length
          ? {
              ticks: {
                font: {
                  size: 12,
                  weight: "bold",
                },
                callback: (value: any) => {
                  return parseInt(value);
                },
              },
              beginAtZero: true,
              type: "linear" as const,
              display: true,
              max: round(Math.max(...dataPointsY1)) + 500,
              position: "right" as const,
              title: {
                text: speedName || trendsName,
                display: true,
                font: {
                  size: 14,
                  family: 'Poppins,Helvetica,"sans-serif',
                  // weight: "bold",
                },
              },
              grid: {
                drawOnChartArea: false,
              },
            }
          : {
              display: false,
            },
    },
  };
  return (
    <Line ref={chartRef} options={options} data={data} plugins={plugins} />
  );
}
